import { Payer } from "@redotech/congo-enums/payer";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  type Adjustment,
  type AllowedExchangeTypes,
  type InputAnswerPartWithStep,
  Step,
  type UncoveredPayer,
} from "./return-flow";
import { FieldId, InputPartType } from "./return-flow-answers";
import { StoreCreditType } from "./return-flow/return-option-enums";

// Zod schemas
const AdjustmentZodSchema_ = z.object({
  flat: z.number(),
  proportion: z.number(),
  currencyOverrides: z.record(z.string(), z.number()).optional(),
  perCurrencyAmountsEnabled: z.boolean().optional(),
});
export const AdjustmentZodSchema: z.ZodType<Adjustment> = AdjustmentZodSchema_;
export interface AdjustmentZod extends z.infer<typeof AdjustmentZodSchema_> {}

const UncoveredPayerZodSchema_ = z.union([
  z.literal(Payer.Customer),
  z.literal(Payer.Merchant),
  z.null(),
]);
export const UncoveredPayerZodSchema: z.ZodType<UncoveredPayer> =
  UncoveredPayerZodSchema_;

const AllowedExchangeTypesZodSchema_ = z.object({
  sameItem: z.boolean(),
  variant: z.boolean(),
  exchangeGroups: z.boolean(),
  advanced: z.boolean(),
});
export const AllowedExchangeTypesZodSchema: z.ZodType<AllowedExchangeTypes> =
  AllowedExchangeTypesZodSchema_;
export interface AllowedExchangeTypesZod extends z.infer<
  typeof AllowedExchangeTypesZodSchema_
> {}

const RepairFeeAmountTypeZodSchema = createEnum(Step.RepairFeeAmountType, {
  enumName: "RepairFeeAmountType",
});

const RepairFeeConfigZodSchema_ = z.discriminatedUnion("strategy", [
  z.object({
    strategy: z.literal(Step.RepairFeeMode.Instant),
    amount: z.number(),
    amountType: RepairFeeAmountTypeZodSchema.optional(),
  }),
  z.object({
    strategy: z.literal(Step.RepairFeeMode.Deferred),
    minPrice: z.number(),
    maxPrice: z.number(),
    amountType: RepairFeeAmountTypeZodSchema.optional(),
  }),
  z.object({ strategy: z.literal(Step.RepairFeeMode.Free) }),
]);
export const RepairFeeConfigZodSchema: z.ZodType<Step.RepairFeeConfig> =
  RepairFeeConfigZodSchema_;

export const ReturnOptionMethodZodSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("credit"),
    creditType: createEnum(StoreCreditType, { enumName: "StoreCreditType" }),
  }),
  z.object({ type: z.literal("exchange") }),
  z.object({ type: z.literal("refund") }),
  z.object({ type: z.literal("repair") }),
]);

const ReturnOptionZodSchema_ = z.object({
  method: ReturnOptionMethodZodSchema,
  labelPayer: UncoveredPayerZodSchema,
  ship: z.boolean(),
  manual_label: z.boolean().optional(),
  exchangeTypes: AllowedExchangeTypesZodSchema,
  chargeCustomerForNewOrderShipping: z.boolean().optional(),
  adjustment: AdjustmentZodSchema,
  chargeRepairFee: z.boolean().optional(),
  repairFee: RepairFeeConfigZodSchema.optional(),
  variantExchangeAddOn: z.boolean().optional(),
  variantExchangeAddOnVariantId: z.string().optional(),
  variantExchangeAddOnTitle: z.string().optional(),
  variantExchangeAddOnAcceptButton: z.string().optional(),
  variantExchangeAddOnRejectButton: z.string().optional(),
  refundPointsRedeemedForItem: z.boolean().optional(),
  deductEarnedPointsFromRefund: z.boolean().optional(),
  reimburseForFinalSale: z.boolean().optional(),
  isUseItOrLoseIt: z.boolean().optional(),
  overrideAdjustmentWithCode: z.boolean().optional(),
  discountTitle: z.string().optional(),
  carryOverLineItemProperties: z.boolean().optional(),
  carryOverLineItemPropertiesOnAddOnAccept: z.boolean().optional(),
  exchangeOrderTags: z.array(z.string()).optional(),
  shopNowBonusEligible: z.boolean().optional(),
});
export const ReturnOptionZodSchema: z.ZodType<ReturnOptionZod> =
  ReturnOptionZodSchema_;
export interface ReturnOptionZod extends z.infer<
  typeof ReturnOptionZodSchema_
> {}

const InputAnswerPartWithStepSchema_ = z.object({
  message: z.string(),
  value: z.union([z.string(), z.array(z.any())]),
  type: createEnum(InputPartType, { enumName: "InputPartType" }),
  fieldId: createEnum(FieldId, { enumName: "FieldId" }).optional(),
  step: z.number(),
});
export const InputAnswerPartWithStepSchema: z.ZodType<InputAnswerPartWithStep> =
  InputAnswerPartWithStepSchema_;
